'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocale } from 'next-intl';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { cn } from '@/lib/utils';

/**
 * Mobile GSAP ScrollTrigger pinned horizontal scroll. The section pins to the
 * top of the viewport; as you keep scrolling the row of cards scrubs sideways
 * (ease "none", 1:1 with the scroll), and once the row is through the pin
 * releases and the page carries on. Under reduced motion it degrades to a plain
 * swipe scroller (no pin, no scroll-jack). Desktop layouts live in each section
 * — this is rendered on the mobile branch only.
 */
export function PinnedRow({
  heading,
  children,
  trackClassName,
  // The section is CSS-hidden above this width; the pin must ONLY run when it is
  // actually the visible layout, or GSAP leaves a giant pin-spacer on desktop.
  query = '(max-width: 1023.98px)',
}: {
  heading?: ReactNode;
  children: ReactNode;
  trackClassName?: string;
  query?: string;
}) {
  const [active, setActive] = useState(false);
  const [reduce, setReduce] = useState(false);
  const isRtl = useLocale() === 'ar';
  useEffect(() => {
    const mqA = window.matchMedia(query);
    const mqR = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => {
      setActive(mqA.matches);
      setReduce(mqR.matches);
    };
    update();
    mqA.addEventListener('change', update);
    mqR.addEventListener('change', update);
    return () => {
      mqA.removeEventListener('change', update);
      mqR.removeEventListener('change', update);
    };
  }, [query]);

  const pinRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active || reduce) return;
    const pin = pinRef.current;
    const track = trackRef.current;
    if (!pin || !track) return;

    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      // Distance the track must travel to reveal its overflow past the viewport.
      const amount = () => Math.max(0, track.scrollWidth - window.innerWidth);
      // In RTL the flex track lays out right-to-left, so its overflow extends
      // past the LEFT edge and the reveal must translate rightward (+x); in LTR
      // it's the mirror (-x). Same sign convention as scroll-hijack-row.
      const sign = isRtl ? 1 : -1;
      // ease "none" keeps horizontal position 1:1 with scroll (required).
      const tween = gsap.to(track, { x: () => sign * amount(), ease: 'none' });
      ScrollTrigger.create({
        trigger: pin,
        start: 'top top',
        // 1.15× travel: the row unwinds in barely more scroll than its own
        // width, so you get through it fast without it feeling hair-trigger.
        end: () => '+=' + amount() * 1.15,
        pin: true,
        anticipatePin: 1,
        scrub: 0.6,
        animation: tween,
        invalidateOnRefresh: true,
      });
    }, pin);
    return () => ctx.revert();
  }, [active, reduce, isRtl]);

  if (reduce) {
    return (
      <div className="flex flex-col gap-8">
        {heading}
        <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-2 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {children}
        </div>
      </div>
    );
  }

  return (
    <div ref={pinRef} className="flex h-svh flex-col justify-center gap-6 overflow-hidden py-10">
      {heading}
      {/* Explicit direction so the overflow side always matches the tween sign,
          even if an ancestor overrides dir. */}
      <div
        ref={trackRef}
        className={cn('flex w-max', trackClassName)}
        style={{ direction: isRtl ? 'rtl' : 'ltr' }}
      >
        {children}
      </div>
    </div>
  );
}
